import { z } from 'zod'
import { PAGE_NEEDS_LIMIT, paginatedQuery } from './pagination.input.js'
import { prismImportJobConfigSchema } from '../dto/prism-import.dto.js'
import { prismEventRefSchema } from '../helpers/prism-event-ref.js'

/**
 * Start a temporary Social Tables → CSE import job.
 *
 * `eventIds` accepts what a user can actually copy — a Social Tables event URL
 * or uid, or a Prismm seating / SAML URL — and stores a uid or `prismm:<id>`
 * either way, so the job config stays one shape no matter how the request was made.
 */
export const startPrismImportInputSchema = prismImportJobConfigSchema
  // Set only by re-importing from the catalog, never by whoever starts a job.
  .omit({ replacesEventId: true })
  .extend({ eventIds: z.array(prismEventRefSchema).optional() })
  .strict()
  .refine(
    (v) =>
      (v.eventIds !== undefined && v.eventIds.length > 0) ||
      (v.limit !== undefined && v.limit > 0) ||
      (v.venueName !== undefined && v.venueName.length > 0),
    { message: 'Provide event links, a venue name, or a list limit to select Social Tables events.' },
  )

export type StartPrismImportInput = z.infer<typeof startPrismImportInputSchema>

/**
 * One Social Tables cookie Value, as copied from DevTools. Quotes get stripped
 * because they are how a Value column sometimes gets copied; a JSON object is
 * rejected because that is the old paste shape, not a cookie.
 */
function stCookieValue(cookieName: string) {
  return z
    .string()
    .trim()
    .min(1, `Paste the ${cookieName} cookie.`)
    .max(8192, `That is longer than a Social Tables cookie.`)
    .transform((v) => v.replace(/^["']|["']$/g, '').trim())
    .refine((v) => v.length > 0, { message: `Paste the ${cookieName} cookie.` })
    .refine((v) => !v.startsWith('{'), { message: 'Just the Value column, not the JSON object.' })
}

/**
 * Replace the stored Social Tables session with the two cookies that actually
 * sign in: `session` and `session.sig` on `.socialtables.com`. The API turns
 * those into a Playwright storage state — the JSON object is not the user's job.
 */
export const savePrismSessionInputSchema = z
  .object({
    session: stCookieValue('session'),
    sessionSig: stCookieValue('session.sig'),
  })
  .strict()

export type SavePrismSessionInput = z.infer<typeof savePrismSessionInputSchema>

/**
 * Clear this organization's imported data so an import can start from a known
 * state. Irreversible, so the word has to be sent: it is the difference between a
 * request that meant to do this and one that arrived by accident.
 */
export const wipeTenantDataInputSchema = z
  .object({ confirm: z.literal('WIPE', { message: 'Type WIPE to confirm.' }) })
  .strict()

export type WipeTenantDataInput = z.infer<typeof wipeTenantDataInputSchema>

/** Record, or withdraw, a person's word that an imported plan is accurate. */
export const setPrismCatalogVerifiedInputSchema = z.object({ verified: z.boolean() }).strict()

export type SetPrismCatalogVerifiedInput = z.infer<typeof setPrismCatalogVerifiedInputSchema>

/**
 * What a catalog listing is asking for.
 *
 * `onlyUnverified` is the panel's "only what still needs attention" — an
 * explicit `'true'`/`'false'` rather than a coerced boolean, because
 * `Boolean('false')` is `true` and a query string carries only strings. A
 * filter that silently means its opposite is worse than one that is rejected.
 */
export const prismCatalogQuerySchema = paginatedQuery({
  onlyUnverified: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
}).refine(...PAGE_NEEDS_LIMIT)

export type PrismCatalogQuery = z.infer<typeof prismCatalogQuerySchema>
